import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Select, MenuItem, Stack, Dialog, DialogTitle, DialogContent, Typography, Alert, CircularProgress, TextField } from '@mui/material';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadReports, createReport } from './reportsSlice';
import { downloadReportFile, fetchReport, deleteReport } from '../../services/api/reportsService';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = { generating: tokens.severity.info, ready: tokens.severity.low, failed: tokens.severity.critical };
const REPORT_TYPES = ['SECURITY_SUMMARY', 'INCIDENT_SUMMARY', 'ASSET_INVENTORY'];

function printReport(report) {
  if (!report?.contentJson) return;
  let content = report.contentJson;
  try { content = JSON.stringify(JSON.parse(report.contentJson), null, 2); } catch { /* keep original */ }
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const win = window.open('', '_blank', 'noopener,noreferrer');
  if (!win) return;
  win.document.write(`<!doctype html><html><head><title>${escapeHtml(report.title || 'CyberGuard Report')}</title><style>body{font-family:Arial,sans-serif;margin:32px;color:#111}h1{font-size:22px}pre{white-space:pre-wrap;word-break:break-word;font:12px/1.5 monospace}@media print{button{display:none}}</style></head><body><h1>${escapeHtml(report.title || 'CyberGuard Report')}</h1><p>Melsoft CyberGuard</p><pre>${escapeHtml(content)}</pre><script>window.onload=()=>{window.print();window.close();}<\/script></body></html>`);
  win.document.close();
}

export default function ReportsPage() {
  const dispatch = useDispatch();
  const { items, loadStatus, createStatus, loadError } = useSelector((s) => s.reports);
  const [type, setType] = useState('SECURITY_SUMMARY');
  const [selected, setSelected] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [downloading, setDownloading] = useState('');
  const [deleting, setDeleting] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filters, setFilters] = useState({});
  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  useEffect(() => { dispatch(loadReports()); }, [dispatch]);

  const normalizedItems = items.map((r) => ({ ...r, status: String(r.status || '').toLowerCase(), type: String(r.type || '').toUpperCase() }));
  const ready = normalizedItems.filter((r) => r.status === 'ready').length;

  const handleGenerate = async () => {
    setActionError('');
    try {
      if (fromDate && toDate && fromDate > toDate) { setActionError('From date cannot be after To date.'); return; }
      await dispatch(createReport({ title: `${type.replace(/_/g, ' ')} Report`, type, fromDate: fromDate || null, toDate: toDate || null, ...filters })).unwrap();
      await dispatch(loadReports()).unwrap();
    } catch { setActionError('Report generation failed. Please retry.'); }
  };

  const retryReport = async (report) => {
    setActionError('');
    try {
      if (fromDate && toDate && fromDate > toDate) { setActionError('From date cannot be after To date.'); return; }
      await dispatch(createReport({ title: report.title, type: report.type, fromDate: fromDate || null, toDate: toDate || null, ...filters })).unwrap();
      await dispatch(loadReports()).unwrap();
    } catch { setActionError('Report retry failed. Review the current filters and try again.'); }
  };

  const handlePreview = async (report) => {
    setActionError(''); setPreviewLoading(true);
    try { setSelected(await fetchReport(report.id)); }
    catch { setActionError('Report preview could not be loaded. Please retry.'); }
    finally { setPreviewLoading(false); }
  };

  const handlePrint = async (report) => {
    setActionError('');
    try { printReport(await fetchReport(report.id)); }
    catch { setActionError('Printable report could not be loaded. Please retry.'); }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete "${report.title}"? This cannot be undone.`)) return;
    setActionError(''); setDeleting(report.id);
    try {
      await deleteReport(report.id);
      if (selected?.id === report.id) setSelected(null);
      await dispatch(loadReports()).unwrap();
    } catch { setActionError('Report could not be deleted. Please retry.'); }
    finally { setDeleting(''); }
  };

  const handleDownload = async (report, format) => {
    const key = `${report.id}:${format}`;
    setActionError(''); setDownloading(key);
    try { await downloadReportFile(report.id, format); }
    catch { setActionError(`${format.toUpperCase()} download failed. Please retry.`); }
    finally { setDownloading(''); }
  };


  return (
    <Box>
      <PageHeader title="Reports & Analytics" subtitle="Generates a structured data snapshot for board/client reporting — security summaries, incident reports, asset inventories" />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total Reports" value={normalizedItems.length} icon={AssessmentRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Ready" value={ready} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Generating" value={normalizedItems.filter((r) => r.status === 'generating').length} accent={tokens.severity.info} /></Grid>
      </Grid>

      {(loadError || actionError) && <Alert severity="error" sx={{ mb: 2 }}>{actionError || 'Reports could not be loaded. Please retry.'}</Alert>}
      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          <Select size="small" value={type} onChange={(e) => { setType(e.target.value); setFilters({}); }} sx={{ minWidth: 220 }}>
            {REPORT_TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
          </Select>
          {type !== 'ASSET_INVENTORY' && <>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </>}
          {type === 'ASSET_INVENTORY' && <>
            <TextField size="small" label="Asset name from" value={filters.assetNameFrom || ''} onChange={setFilter('assetNameFrom')} />
            <TextField size="small" label="Asset name to" value={filters.assetNameTo || ''} onChange={setFilter('assetNameTo')} />
            <Select size="small" displayEmpty value={filters.assetType || ''} onChange={setFilter('assetType')}><MenuItem value="">All asset types</MenuItem>{['SERVER','WORKSTATION','NETWORK_DEVICE','CLOUD_RESOURCE','APPLICATION','MOBILE_DEVICE'].map(v=><MenuItem key={v} value={v}>{v.replace(/_/g,' ')}</MenuItem>)}</Select>
            <Select size="small" displayEmpty value={filters.assetCriticality || ''} onChange={setFilter('assetCriticality')}><MenuItem value="">All criticalities</MenuItem>{['LOW','MEDIUM','HIGH','CRITICAL'].map(v=><MenuItem key={v} value={v}>{v}</MenuItem>)}</Select>
            <Select size="small" displayEmpty value={filters.assetStatus || ''} onChange={setFilter('assetStatus')}><MenuItem value="">All asset statuses</MenuItem>{['ACTIVE','UNDER_MAINTENANCE','DECOMMISSIONED'].map(v=><MenuItem key={v} value={v}>{v.replace(/_/g,' ')}</MenuItem>)}</Select>
            <TextField size="small" label="Owner contains" value={filters.assetOwner || ''} onChange={setFilter('assetOwner')} />
            <TextField size="small" label="Location contains" value={filters.assetLocation || ''} onChange={setFilter('assetLocation')} />
          </>}
          {type === 'INCIDENT_SUMMARY' && <>
            <Select size="small" displayEmpty value={filters.incidentSeverity || ''} onChange={setFilter('incidentSeverity')}><MenuItem value="">All severities</MenuItem>{['LOW','MEDIUM','HIGH','CRITICAL'].map(v=><MenuItem key={v} value={v}>{v}</MenuItem>)}</Select>
            <Select size="small" displayEmpty value={filters.incidentStatus || ''} onChange={setFilter('incidentStatus')}><MenuItem value="">All incident statuses</MenuItem>{['OPEN','INVESTIGATING','CONTAINED','RESOLVED'].map(v=><MenuItem key={v} value={v}>{v}</MenuItem>)}</Select>
            <TextField size="small" label="Source module contains" value={filters.sourceModule || ''} onChange={setFilter('sourceModule')} />
          </>}
          <Button variant="contained" onClick={handleGenerate} disabled={createStatus === 'creating'}>{createStatus === 'creating' ? <><CircularProgress size={16} sx={{ mr: 1 }} />Generating…</> : 'Generate Report'}</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Title', 'Type', 'Status', 'Generated', 'Requested', 'Actions'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadStatus === 'loading' && <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={22} /></TableCell></TableRow>}
            {loadStatus !== 'loading' && normalizedItems.length === 0 && <TableRow><TableCell colSpan={6} align="center">No reports have been generated yet.</TableCell></TableRow>}
            {normalizedItems.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontSize: '0.8rem' }}>{r.title}</TableCell>
                <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.muted }}>{r.type.replace(/_/g, ' ')}</TableCell>
                <TableCell><Chip label={r.status} size="small" sx={{ color: STATUS_COLOR[r.status], border: `1px solid ${STATUS_COLOR[r.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>{r.generatedAt ? formatRelativeTime(r.generatedAt) : '—'}</TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(r.createdAt)}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" disabled={r.status !== 'ready'} onClick={() => handlePreview(r)}>Preview</Button>
                    <Button size="small" disabled={r.status !== 'ready'} onClick={() => handlePrint(r)}>Print</Button>
                    <Button size="small" disabled={r.status !== 'ready' || Boolean(downloading)} onClick={() => handleDownload(r, 'pdf')}>{downloading === `${r.id}:pdf` ? 'PDF…' : 'PDF'}</Button>
                    <Button size="small" disabled={r.status !== 'ready' || Boolean(downloading)} onClick={() => handleDownload(r, 'docx')}>{downloading === `${r.id}:docx` ? 'Word…' : 'Word'}</Button>
                    <Button size="small" disabled={r.status !== 'ready' || Boolean(downloading)} onClick={() => handleDownload(r, 'xlsx')}>{downloading === `${r.id}:xlsx` ? 'Excel…' : 'Excel'}</Button>
                    <Button size="small" disabled={r.status !== 'ready' || Boolean(downloading)} onClick={() => handleDownload(r, 'csv')}>{downloading === `${r.id}:csv` ? 'CSV…' : 'CSV'}</Button>
                    {r.status === 'failed' && <Button size="small" onClick={() => retryReport(r)}>Retry</Button>}
                    <Button size="small" disabled={Boolean(deleting)} onClick={() => handleDelete(r)}>{deleting === r.id ? 'Deleting…' : 'Delete'}</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Dialog open={Boolean(selected) || previewLoading} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>{previewLoading ? 'Loading report…' : (selected?.title || 'Report')}</DialogTitle>
        <DialogContent dividers>
          {previewLoading ? <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box> : <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.78rem' }}>
            {selected?.failureReason ? 'Report generation failed. Please retry or contact your administrator.' : selected?.contentJson ? (() => {
              try { return JSON.stringify(JSON.parse(selected.contentJson), null, 2); }
              catch { return selected.contentJson; }
            })() : 'No report content is available.'}
          </Typography>}
        </DialogContent>
        {!previewLoading && <Stack direction="row" spacing={1} sx={{ p: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => printReport(selected)} disabled={!selected?.contentJson}>Print</Button>
          <Button onClick={() => selected && handleDownload(selected, 'pdf')} disabled={!selected?.contentJson}>PDF</Button>
          <Button onClick={() => selected && handleDownload(selected, 'docx')} disabled={!selected?.contentJson}>Word</Button>
          <Button onClick={() => selected && handleDownload(selected, 'xlsx')} disabled={!selected?.contentJson}>Excel</Button>
          <Button onClick={() => selected && handleDownload(selected, 'csv')} disabled={!selected?.contentJson}>CSV</Button>
        </Stack>}
      </Dialog>
    </Box>
  );
}
